import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { BadgeComponent } from 'shared/components/BadgeComponent.tsx';
import { GalleryGroupRarityCounts, GalleryRarityCounts } from 'shared/components/RarityCountsDisplay';
import { getAutoUpdatedMap } from 'shared/hooks/getAutoUpdatedMap.ts';
import { Badge } from 'features/catalog/useBadges.ts';
import {
  DEFAULT_BADGE_DATE_ADDED,
  isBadgeReleased,
  isDateAddedVisible,
  parseDateAdded,
} from 'features/feed/useFeedUpdates.ts';
import { BadgeModes } from 'types/badge-modes';
import { compareBadgesByClaimPercentDesc, formatBadgeClaimPercent } from 'features/claim/badge-claim-eligibility.ts';
import { BADGE_DESCRIPTIONS, isSoftHiddenBySiblingOffsite } from 'features/catalog/badge-definitions.ts';
import { getBadgeProperties } from 'shared/components/getBadgeProperties';
import {
  BADGE_GROUP_BADGE_IDS,
  BADGE_GROUPS,
  groupBackgroundGradient,
  groupNameForBadgeId,
  slugifyGroupName,
  sortBadgeGroupsByGlobalClaimRate,
} from 'features/catalog/badge-groups.ts';
import { getBadgeCollectionGroup, setBadgeCollectionGroup } from 'features/catalog/badge-collection-group.ts';
import {
  badgeIdsForJourneyNameQuery,
  badgeIdsInJourney,
  getBadgesThatCompleteJourneys,
  isBadgeEligibleForClaimedByOthers,
  JOURNEYS,
} from 'features/catalog/badge-journeys.ts';
import { activeProfileBadgeIDs, getInitialBadgeEnabledState } from 'features/catalog/badge-state.ts';
import {
  getExtraBadgeDescription,
  isBadgeClaimableAgainstOwnership,
  isEditGroupRarityCountable,
} from 'features/claim/badge-unlock-hint.ts';
import { BADGE_SUBMISSION_URL } from 'config/data-sources';
import {
  EMPTY_EDIT_LAYOUT_FROZEN_PREFIX,
  resolveEditProfileGroupLayout,
  type EditLayoutFrozenPrefix,
} from 'features/claim/edit-group-layout.ts';
import {
  getClaimedByOthersCollapsed,
  getJourneyClaimCollapsed,
  getRecentlyAddedCollapsed,
  setClaimedByOthersCollapsed,
  setJourneyClaimCollapsed,
  setRecentlyAddedCollapsed,
} from 'features/claim/journey-claim-collapsed.ts';
import {
  compareBadgesByScoreDesc,
  compareForceSortLast,
  countUniqueJourneyRarities,
  getRarityScoreFromName,
  RARITY_ORDER,
} from 'features/catalog/rarity';
import '../../features/gallery/BadgeGalleryView.css';
import './BadgeGroupsComponent.css';

export { getBadgeProperties } from 'shared/components/getBadgeProperties';

type BadgeRarityName = (typeof RARITY_ORDER)[number];

export const BadgeGroupsComponent = ({
  profileBadgeIDs,
  badgesMap,
  mode,
  onClickBadge,
  setSaveDisabled,
  setEnabledBadgesForClipboard,
  showPercentages = true,
  badgeCreditsSubmissions,
  badgeDesignSubmissions,
  email,
  enabledBadgesMap: sharedEnabledBadgesMap,
  setEnabledBadgesMap: setSharedEnabledBadgesMap,
  handleBadgeToggle,
  editLayoutFrozenPrefix = EMPTY_EDIT_LAYOUT_FROZEN_PREFIX,
  visibleGroups,
  rarityFilters,
  showFreeformBadges = true,
  showJourneyCompletingSection = true,
  showClaimedByOthersSection = true,
  showRecentlyAddedSection = true,
  profileLastUpdated = null,
  badgeSearchQuery = '',
}: {
  profileBadgeIDs: string[] | null;
  badgesMap: Map<string, Badge>;
  mode: BadgeModes;
  onClickBadge: (badge: Badge) => void;
  setSaveDisabled: (disabled: boolean) => void;
  setEnabledBadgesForClipboard: (enabledBadges: string[]) => void;
  showPercentages: boolean;
  badgeCreditsSubmissions: string[];
  badgeDesignSubmissions: string[];
  email: string;
  enabledBadgesMap?: Map<string, boolean>;
  setEnabledBadgesMap?: (map: Map<string, boolean>) => void;
  handleBadgeToggle?: (badge: Badge, fromSuggestion?: boolean) => void;
  /** Groups at/above the last claim — parent owns this so nav and sections stay aligned. */
  editLayoutFrozenPrefix?: EditLayoutFrozenPrefix;
  visibleGroups?: typeof BADGE_GROUPS;
  /** Active rarity pills; empty set means all rarities. */
  rarityFilters?: ReadonlySet<BadgeRarityName>;
  /** When false, hide the ungrouped/freeform badge section. Default true. */
  showFreeformBadges?: boolean;
  /** When false, hide the “claim to complete a journey” section. Default true. */
  showJourneyCompletingSection?: boolean;
  /** When false, hide the “claimed by others” suggestions section. Default true. */
  showClaimedByOthersSection?: boolean;
  /** When false, hide the “recently added” suggestions section. Default true. */
  showRecentlyAddedSection?: boolean;
  /** Profile’s last claim submission time — gates “recently added” vs catalog dateAdded. */
  profileLastUpdated?: Date | null;
  /** Live claim-page search string (filters groups / badges). */
  badgeSearchQuery?: string;
}) => {
  const [localEnabledBadgesMap, setLocalEnabledBadgesMap] = useState(new Map<string, boolean>());
  const [journeyClaimCollapsed, setJourneyClaimCollapsedState] = useState(() => getJourneyClaimCollapsed());
  const [claimedByOthersCollapsed, setClaimedByOthersCollapsedState] = useState(() => getClaimedByOthersCollapsed());
  const [recentlyAddedCollapsed, setRecentlyAddedCollapsedState] = useState(() => getRecentlyAddedCollapsed());
  /** Expanded “Claimed” count tiles (group name or `freeform`). Default collapsed. */
  const [expandedClaimedPartitions, setExpandedClaimedPartitions] = useState(() => new Set<string>());
  /** Expanded “hidden” count tiles (locked / not claimable). Default collapsed. */
  const [expandedHiddenPartitions, setExpandedHiddenPartitions] = useState(() => new Set<string>());

  const scrollUnlockTargetIntoView = useCallback((el: Element) => {
    const scroller = document.querySelector('.app-shell-main');
    const header = document.querySelector('.claim-badges-header');
    const pad = header instanceof HTMLElement ? header.getBoundingClientRect().height + 12 : 24;
    if (scroller instanceof HTMLElement) {
      const top = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - pad;
      scroller.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      return;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  /** Group name to scroll to after switching category pills (section wasn’t mounted yet). */
  const pendingUnlockScrollGroupRef = useRef<string | null>(null);

  // Use shared state if available, otherwise use local state
  const enabledBadgesMap = sharedEnabledBadgesMap || localEnabledBadgesMap;
  const setEnabledBadgesMap = setSharedEnabledBadgesMap || setLocalEnabledBadgesMap;

  /** Suggestion-section claim: keep the tile in place while it colors up and fades out. */
  const SUGGESTION_DEPART_MS = 560;
  const [departingSuggestionIds, setDepartingSuggestionIds] = useState(() => new Set<string>());
  /** Frozen order while any tile is departing — refill only after the fade finishes. */
  const [frozenClaimedByOthers, setFrozenClaimedByOthers] = useState<Badge[] | null>(null);
  const [frozenJourneySuggestions, setFrozenJourneySuggestions] = useState<
    { badge: Badge; journey: (typeof JOURNEYS)[number] }[] | null
  >(null);
  const [frozenRecentlyAdded, setFrozenRecentlyAdded] = useState<Badge[] | null>(null);
  const matchingClaimedByOthersRef = useRef<Badge[]>([]);
  const matchingJourneySuggestionsRef = useRef<{ badge: Badge; journey: (typeof JOURNEYS)[number] }[]>([]);
  const matchingRecentlyAddedRef = useRef<Badge[]>([]);
  const frozenClaimedByOthersRef = useRef<Badge[] | null>(null);
  const frozenJourneySuggestionsRef = useRef<{ badge: Badge; journey: (typeof JOURNEYS)[number] }[] | null>(null);
  const frozenRecentlyAddedRef = useRef<Badge[] | null>(null);
  const suggestionDepartTimersRef = useRef<Map<string, number>>(new Map());
  const departingClaimedByOthersIdsRef = useRef(new Set<string>());
  const departingJourneyIdsRef = useRef(new Set<string>());
  const departingRecentlyAddedIdsRef = useRef(new Set<string>());
  const liveOwnedBadgeIds = useMemo(
    () => [...enabledBadgesMap.entries()].filter(([, enabled]) => enabled).map(([id]) => id),
    [enabledBadgesMap]
  );

  useEffect(() => {
    const timers = suggestionDepartTimersRef.current;
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const beginSuggestionDepart = useCallback((badgeId: string) => {
    const othersPool = frozenClaimedByOthersRef.current ?? matchingClaimedByOthersRef.current;
    const journeyPool = frozenJourneySuggestionsRef.current ?? matchingJourneySuggestionsRef.current;
    const recentlyPool = frozenRecentlyAddedRef.current ?? matchingRecentlyAddedRef.current;
    const othersBadge = othersPool.find((badge) => badge.id === badgeId);
    const journeyEntry = journeyPool.find((entry) => entry.badge.id === badgeId);
    const recentlyBadge = recentlyPool.find((badge) => badge.id === badgeId);
    if (!othersBadge && !journeyEntry && !recentlyBadge) return;

    if (othersBadge) {
      if (!frozenClaimedByOthersRef.current) {
        const snapshot = [...othersPool];
        frozenClaimedByOthersRef.current = snapshot;
        setFrozenClaimedByOthers(snapshot);
      }
      departingClaimedByOthersIdsRef.current.add(badgeId);
    }
    if (journeyEntry) {
      if (!frozenJourneySuggestionsRef.current) {
        const snapshot = [...journeyPool];
        frozenJourneySuggestionsRef.current = snapshot;
        setFrozenJourneySuggestions(snapshot);
      }
      departingJourneyIdsRef.current.add(badgeId);
    }
    if (recentlyBadge) {
      if (!frozenRecentlyAddedRef.current) {
        const snapshot = [...recentlyPool];
        frozenRecentlyAddedRef.current = snapshot;
        setFrozenRecentlyAdded(snapshot);
      }
      departingRecentlyAddedIdsRef.current.add(badgeId);
    }

    setDepartingSuggestionIds((prev) => {
      if (prev.has(badgeId)) return prev;
      const next = new Set(prev);
      next.add(badgeId);
      return next;
    });

    const existing = suggestionDepartTimersRef.current.get(badgeId);
    if (existing) window.clearTimeout(existing);
    const timer = window.setTimeout(() => {
      suggestionDepartTimersRef.current.delete(badgeId);
      setDepartingSuggestionIds((prev) => {
        if (!prev.has(badgeId)) return prev;
        const next = new Set(prev);
        next.delete(badgeId);
        return next;
      });
      departingClaimedByOthersIdsRef.current.delete(badgeId);
      departingJourneyIdsRef.current.delete(badgeId);
      departingRecentlyAddedIdsRef.current.delete(badgeId);
      if (departingClaimedByOthersIdsRef.current.size === 0) {
        frozenClaimedByOthersRef.current = null;
        setFrozenClaimedByOthers(null);
      }
      if (departingJourneyIdsRef.current.size === 0) {
        frozenJourneySuggestionsRef.current = null;
        setFrozenJourneySuggestions(null);
      }
      if (departingRecentlyAddedIdsRef.current.size === 0) {
        frozenRecentlyAddedRef.current = null;
        setFrozenRecentlyAdded(null);
      }
    }, SUGGESTION_DEPART_MS);
    suggestionDepartTimersRef.current.set(badgeId, timer);
  }, []);

  const submittedBadgeIDs = useMemo(() => {
    // Unique badge IDs — idea + design on the same badge counts once (Badge Baron / Badger).
    return [...new Set([...badgeCreditsSubmissions, ...badgeDesignSubmissions])];
  }, [badgeCreditsSubmissions, badgeDesignSubmissions]);
  const submittedBadgeIDSet = useMemo(() => new Set(submittedBadgeIDs), [submittedBadgeIDs]);
  const badgeCreditSubmissionIDSet = useMemo(() => new Set(badgeCreditsSubmissions), [badgeCreditsSubmissions]);
  const badgeDesignSubmissionIDSet = useMemo(() => new Set(badgeDesignSubmissions), [badgeDesignSubmissions]);
  const profileBadgeIDSet = useMemo(() => new Set(profileBadgeIDs ?? []), [profileBadgeIDs]);

  // Ids of badges to highlight because the "Requires" hint referencing them is currently hovered.
  // Array preserves requiredBadgeIds order (wave order falls back to this, then visual LTR).
  const [hoveredUnlockRequirementIds, setHoveredUnlockRequirementIds] = useState<string[]>([]);
  const hoveredUnlockRequirementIdSet = useMemo(
    () => new Set(hoveredUnlockRequirementIds),
    [hoveredUnlockRequirementIds]
  );
  const setUnlockRequirementsHovered = useCallback((requiredBadgeIds: string[] | undefined, isHovered: boolean) => {
    if (!requiredBadgeIds || requiredBadgeIds.length === 0) return;
    setHoveredUnlockRequirementIds((prev) => {
      if (isHovered) {
        const prevSet = new Set(prev);
        const added = requiredBadgeIds.filter((id) => !prevSet.has(id));
        return added.length === 0 ? prev : [...prev, ...added];
      }
      const remove = new Set(requiredBadgeIds);
      const next = prev.filter((id) => !remove.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, []);

  // Soft nudge waves through required keys one-by-one, then pauses, then repeats.
  const [unlockWaveActiveId, setUnlockWaveActiveId] = useState<string | null>(null);

  useEffect(() => {
    const ids = hoveredUnlockRequirementIds;
    if (ids.length === 0) {
      setUnlockWaveActiveId(null);
      return;
    }

    // Prefer left-to-right / top-to-bottom visual order so the wave reads across the grid.
    let ordered = ids;
    if (ids.length > 1 && typeof document !== 'undefined') {
      const withPos = ids.map((id) => {
        const el = document.querySelector(`[data-badge-id="${CSS.escape(id)}"]`);
        const rect = el?.getBoundingClientRect();
        return {
          id,
          top: rect?.top ?? Number.POSITIVE_INFINITY,
          left: rect?.left ?? Number.POSITIVE_INFINITY,
          missing: !rect,
        };
      });
      // Keep requiredBadgeIds order for badges not currently in the DOM (collapsed hidden stack).
      withPos.sort((a, b) => {
        if (a.missing && b.missing) return ids.indexOf(a.id) - ids.indexOf(b.id);
        if (a.missing) return 1;
        if (b.missing) return -1;
        return a.top - b.top || a.left - b.left;
      });
      ordered = withPos.map((p) => p.id);
    }

    if (ordered.length === 1) {
      setUnlockWaveActiveId(ordered[0]);
      return;
    }

    let cancelled = false;
    let index = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const SLOT_MS = 400;
    const PAUSE_MS = 480;

    const run = () => {
      if (cancelled) return;
      // Hold each key for a full SLOT (including the last) before pausing — don't clear
      // in the same tick as activating the final id, or it never visibly nudges.
      if (index >= ordered.length) {
        setUnlockWaveActiveId(null);
        index = 0;
        timer = setTimeout(run, PAUSE_MS);
        return;
      }
      setUnlockWaveActiveId(ordered[index]);
      index += 1;
      timer = setTimeout(run, SLOT_MS);
    };
    run();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [hoveredUnlockRequirementIds]);

  const handleUnlockTargetClick = useCallback(
    (target: { badgeId?: string; groupName?: string }) => {
      // Click means navigate — stop the key nudge. Category switches can unmount the
      // hovered lock badge without mouseleave, which otherwise leaves an infinite shake.
      setHoveredUnlockRequirementIds([]);
      setUnlockWaveActiveId(null);

      // Always scroll to the category section (Tenure, Offsite Alumni, …) — not the individual badge.
      const groupName = target.groupName ?? (target.badgeId ? groupNameForBadgeId(target.badgeId) : null);
      if (!groupName) return;
      const el = document.querySelector(`[data-gallery-group="${CSS.escape(groupName)}"]`);
      if (el) {
        // Already visible (All, or that category selected) — scroll only; keep All if that’s active.
        scrollUnlockTargetIntoView(el);
        return;
      }
      // Another category pill is filtering this section out — select its pill, then scroll once mounted.
      pendingUnlockScrollGroupRef.current = groupName;
      setBadgeCollectionGroup(slugifyGroupName(groupName));
    },
    [scrollUnlockTargetIntoView]
  );

  useEffect(() => {
    const pending = pendingUnlockScrollGroupRef.current;
    if (!pending) return;
    if (getBadgeCollectionGroup() !== slugifyGroupName(pending)) return;
    const el = document.querySelector(`[data-gallery-group="${CSS.escape(pending)}"]`);
    if (!el) return;
    pendingUnlockScrollGroupRef.current = null;
    requestAnimationFrame(() => scrollUnlockTargetIntoView(el));
  }, [visibleGroups, scrollUnlockTargetIntoView]);

  // Ids of badges whose "Requires" requirement was just satisfied this session — pop briefly, then clear.
  const previouslyLockedIdsRef = useRef<Set<string> | null>(null);
  const [justUnlockedIds, setJustUnlockedIds] = useState(new Set<string>());

  useEffect(() => {
    if (mode !== 'editing_profile' || badgesMap.size === 0) return;

    const currentlyLockedIds = new Set<string>();
    badgesMap.forEach((badge) => {
      if (
        badge.restrictedRules &&
        getExtraBadgeDescription(badge, badgesMap, enabledBadgesMap, submittedBadgeIDs.length)?.hidesDescription
      ) {
        currentlyLockedIds.add(badge.id);
      }
    });

    const previouslyLockedIds = previouslyLockedIdsRef.current;
    previouslyLockedIdsRef.current = currentlyLockedIds;
    if (!previouslyLockedIds) return; // first run - nothing to diff against yet

    const newlyUnlockedIds = [...previouslyLockedIds].filter((id) => !currentlyLockedIds.has(id));
    if (newlyUnlockedIds.length === 0) return;

    setJustUnlockedIds((prev) => new Set([...prev, ...newlyUnlockedIds]));
    const timer = setTimeout(() => {
      setJustUnlockedIds((prev) => {
        const next = new Set(prev);
        newlyUnlockedIds.forEach((id) => next.delete(id));
        return next;
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [badgesMap, enabledBadgesMap, mode, submittedBadgeIDs.length]);

  useEffect(() => {
    if (badgesMap.size === 0) return;
    const allJourneyBadgeIDs = new Set<string>();
    for (const journey of JOURNEYS) {
      for (const id of badgeIdsInJourney(journey)) allJourneyBadgeIDs.add(id);
    }
    // const unused = [...badgesMap.values()]
    //   .filter((b) => !allJourneyBadgeIDs.has(b.id))
    //   .sort((a, b) => (a.badgeScore ?? 0) - (b.badgeScore ?? 0));
    // console.log(
    //   `Badges not in ANY journey (${unused.length}):`,
    //   unused.map((b) => `[${b.rarityName}] ${b.name} (${b.id}) — score: ${b.badgeScore ?? 0}`)
    // );

    // Badges used in multiple journeys
    const badgeCountInJourneyByBadgeID = new Map<string, number>();
    for (const journey of JOURNEYS) {
      for (const id of badgeIdsInJourney(journey)) {
        badgeCountInJourneyByBadgeID.set(id, (badgeCountInJourneyByBadgeID.get(id) || 0) + 1);
      }
    }
    // const badgesUsedInMultipleJourneys = Array.from(badgeCountInJourneyByBadgeID.entries())
    //   .filter(([_, count]) => count > 1)
    //   .map(([id]) => badgesMap.get(id)!);
    // console.log(
    //   `Badges used in multiple journeys (${badgesUsedInMultipleJourneys.length}):`,
    //   badgesUsedInMultipleJourneys.map((b) => `[${b.rarityName}] ${b.name} (${b.id}) — score: ${b.badgeScore ?? 0}`)
    // );
  }, [badgesMap]);

  // set enabled badges to badgeIDS from profileBadgeIDs (only when not using shared state)
  useEffect(() => {
    if (!sharedEnabledBadgesMap) {
      // when email updates, update enabledBadgesMap
      setLocalEnabledBadgesMap(getInitialBadgeEnabledState(profileBadgeIDs, badgesMap));
    }
  }, [email, profileBadgeIDs, badgesMap, sharedEnabledBadgesMap]);

  useEffect(() => {
    if (!sharedEnabledBadgesMap) {
      setEnabledBadgesForClipboard([...enabledBadgesMap.entries()].filter(([_, enabled]) => enabled).map(([id]) => id));
    }
  }, [enabledBadgesMap, setEnabledBadgesForClipboard, sharedEnabledBadgesMap]);

  const handleOnClickBadge = useCallback(
    (badge: Badge, fromSuggestion = false) => {
      if (mode !== 'editing_profile') {
        return onClickBadge?.(badge);
      }

      if (departingSuggestionIds.has(badge.id)) return;

      const claiming = !enabledBadgesMap.get(badge.id);
      if (claiming) beginSuggestionDepart(badge.id);

      // Use shared handler if available, otherwise use local logic
      if (handleBadgeToggle) {
        return handleBadgeToggle(badge, fromSuggestion);
      }

      const baselineIDs = activeProfileBadgeIDs(profileBadgeIDs);
      if (enabledBadgesMap.get(badge.id) && baselineIDs.includes(badge.id)) {
        return;
      }

      setSaveDisabled?.(false);
      // toggle badge enabled state
      let newMap = new Map(enabledBadgesMap);
      newMap.set(badge.id, !enabledBadgesMap.get(badge.id));

      newMap = getAutoUpdatedMap(newMap, badgesMap, {
        submittedBadgeIDs: submittedBadgeIDs,
        email,
      });

      // Keep originally claimed badges, then re-run cascades so auto-claim milestones
      // (Always Down / Explorer / …) still drop when unclaiming unfinished their deps.
      for (const id of baselineIDs) {
        newMap.set(id, true);
      }
      newMap = getAutoUpdatedMap(newMap, badgesMap, {
        submittedBadgeIDs: submittedBadgeIDs,
        email,
      });

      setEnabledBadgesMap(newMap);
    },
    [
      mode,
      handleBadgeToggle,
      setSaveDisabled,
      enabledBadgesMap,
      badgesMap,
      submittedBadgeIDs,
      email,
      setEnabledBadgesMap,
      onClickBadge,
      profileBadgeIDs,
      departingSuggestionIds,
      beginSuggestionDepart,
    ]
  );

  const journeyCompletingBadges = useMemo(() => {
    if (mode !== 'editing_profile') return [];

    const currentBadgeIDs = [...enabledBadgesMap.entries()].filter(([_, enabled]) => enabled).map(([id]) => id);
    const all = getBadgesThatCompleteJourneys(currentBadgeIDs)
      .map(({ badgeId, journey, type }) => ({
        badge: badgesMap.get(badgeId),
        journey,
        type,
      }))
      .filter((entry) => entry.badge != null) as {
      badge: Badge;
      journey: (typeof JOURNEYS)[number];
      type: 'and' | 'or';
    }[];

    const MAX = 6;
    // Suggestion sections sort by claim % (badge groups use badgeScore).
    const sortByClaimPct = (a: { badge: Badge }, b: { badge: Badge }) =>
      compareBadgesByClaimPercentDesc(a.badge, b.badge);

    const isLocked = (badge: Badge) => {
      const { disableInteraction } = getBadgeProperties({
        badge,
        mode,
        enabledBadgesMap,
        badgeCreditsSubmissions,
        badgeDesignSubmissions,
        profileBadgeIDs,
        submittedBadgeIDSet,
        badgeCreditSubmissionIDSet,
        badgeDesignSubmissionIDSet,
        profileBadgeIDSet,
      });
      return disableInteraction;
    };

    const ands = all
      .filter((e) => e.type === 'and' && !isLocked(e.badge))
      .sort(sortByClaimPct)
      .slice(0, MAX);
    if (ands.length >= MAX) return ands;

    const remaining = MAX - ands.length;
    const ors = all
      .filter((e) => e.type === 'or' && !isLocked(e.badge))
      .sort(sortByClaimPct)
      .slice(0, remaining);

    return [...ands, ...ors].sort(sortByClaimPct);
  }, [
    mode,
    profileBadgeIDs,
    enabledBadgesMap,
    badgesMap,
    badgeCreditsSubmissions,
    badgeDesignSubmissions,
    submittedBadgeIDSet,
    badgeCreditSubmissionIDSet,
    badgeDesignSubmissionIDSet,
    profileBadgeIDSet,
  ]);

  // Eligible unclaimed suggestion pool (sorted). UI filters + 12-cap applied below.
  // If the user already claimed an office check-in badge, those stay out of the top 6
  // but may appear in slots 7–12. Office city journeys: step-1 picks always eligible;
  // claiming any of them unlocks the rest of that journey.
  const claimedByOthersEligible = useMemo(() => {
    if (mode !== 'editing_profile') return [];

    const currentBadgeIDs = [...enabledBadgesMap.entries()].filter(([, enabled]) => enabled).map(([id]) => id);

    return [...badgesMap.values()]
      .filter((badge) => {
        if (enabledBadgesMap.get(badge.id)) return false;
        if (!isBadgeReleased(badge.id)) return false;
        if (badge.banSelfClaim) return false;
        if (badge.suppressSuggest) return false;
        // Soft-hide (e.g. other 2023 offsite) collapses into “hidden” — keep out of suggestions too.
        if (isSoftHiddenBySiblingOffsite(badge.id, profileBadgeIDSet)) return false;
        if ((badge.participantIDsWhoOwnThisBadge?.length ?? 0) < 1) return false;
        if (!isBadgeEligibleForClaimedByOthers(badge.id, currentBadgeIDs)) return false;
        return true;
      })
      .filter((badge) => {
        const { disableInteraction } = getBadgeProperties({
          badge,
          mode,
          enabledBadgesMap,
          badgeCreditsSubmissions,
          badgeDesignSubmissions,
          profileBadgeIDs,
          submittedBadgeIDSet,
          badgeCreditSubmissionIDSet,
          badgeDesignSubmissionIDSet,
          profileBadgeIDSet,
        });
        return !disableInteraction;
      })
      .sort(compareBadgesByClaimPercentDesc);
  }, [
    mode,
    badgesMap,
    enabledBadgesMap,
    badgeCreditsSubmissions,
    badgeDesignSubmissions,
    profileBadgeIDs,
    submittedBadgeIDSet,
    badgeCreditSubmissionIDSet,
    badgeDesignSubmissionIDSet,
    profileBadgeIDSet,
  ]);

  // Unclaimed badges added after the user’s last claim, or within the last 2 days
  // (2-day window still shows even if they claimed something after those badges dropped).
  const recentlyAddedEligible = useMemo(() => {
    if (mode !== 'editing_profile') return [];

    const lastUpdatedMs = profileLastUpdated?.getTime() ?? null;
    const twoDaysAgoMs = Date.now() - 2 * 24 * 60 * 60 * 1000;

    return [...badgesMap.values()]
      .filter((badge) => {
        if (enabledBadgesMap.get(badge.id)) return false;
        if (badge.banSelfClaim) return false;
        if (isSoftHiddenBySiblingOffsite(badge.id, profileBadgeIDSet)) return false;
        const desc = BADGE_DESCRIPTIONS.get(badge.id);
        const dateAdded = desc?.dateAdded ?? DEFAULT_BADGE_DATE_ADDED;
        if (!isDateAddedVisible(dateAdded)) return false;
        const addedMs = parseDateAdded(dateAdded).getTime();
        const afterLastClaim = lastUpdatedMs != null && addedMs > lastUpdatedMs;
        const withinLastTwoDays = addedMs >= twoDaysAgoMs;
        if (!afterLastClaim && !withinLastTwoDays) return false;
        return true;
      })
      .filter((badge) => {
        const { disableInteraction } = getBadgeProperties({
          badge,
          mode,
          enabledBadgesMap,
          badgeCreditsSubmissions,
          badgeDesignSubmissions,
          profileBadgeIDs,
          submittedBadgeIDSet,
          badgeCreditSubmissionIDSet,
          badgeDesignSubmissionIDSet,
          profileBadgeIDSet,
        });
        return !disableInteraction;
      })
      .sort(compareBadgesByClaimPercentDesc);
  }, [
    mode,
    profileLastUpdated,
    badgesMap,
    enabledBadgesMap,
    badgeCreditsSubmissions,
    badgeDesignSubmissions,
    profileBadgeIDs,
    submittedBadgeIDSet,
    badgeCreditSubmissionIDSet,
    badgeDesignSubmissionIDSet,
    profileBadgeIDSet,
  ]);

  // Physical office check-ins only (not Explorer / Jet Boots / etc. in Passport Stamps).
  const officeCheckInIds = useMemo(() => new Set(['fk3y', 'ziyg', '56jo', 'zprr', 'qpwb', 'bost']), []);
  const hasClaimedOfficeCheckIn = [...officeCheckInIds].some((id) => enabledBadgesMap.get(id));

  // Only the badges that have a key in badgesMap but are not in any badge group
  const badgesNotInAnyBadgeGroup = useMemo(() => {
    const submittedIDs = mode === 'submitted' ? submittedBadgeIDSet : null;
    return [...badgesMap.keys()].filter(
      (id) => !BADGE_GROUP_BADGE_IDS.has(id) && (submittedIDs == null || submittedIDs.has(id))
    );
  }, [badgesMap, mode, submittedBadgeIDSet]);

  // Render component with a special className to denote if badge is enabled or not
  // Clicking the badge will toggle the enabled state
  const searchQuery = badgeSearchQuery.trim().toLowerCase();
  const journeySearchBadgeIds = useMemo(
    () => (searchQuery ? badgeIdsForJourneyNameQuery(searchQuery) : null),
    [searchQuery]
  );

  const passesSearchFilter = useCallback(
    (badge: Badge, groupName?: string) => {
      if (!searchQuery) return true;
      if (groupName && groupName.toLowerCase().includes(searchQuery)) return true;
      if (journeySearchBadgeIds?.has(badge.id)) return true;
      const description = isBadgeReleased(badge.id) ? badge.description : '';
      return badge.name.toLowerCase().includes(searchQuery) || description.toLowerCase().includes(searchQuery);
    },
    [searchQuery, journeySearchBadgeIds]
  );

  const isClaimableAgainstEnabledMap = useCallback(
    (badge: Badge, ownershipMap: Map<string, boolean>) =>
      isBadgeClaimableAgainstOwnership(badge, badgesMap, ownershipMap, submittedBadgeIDs.length),
    [badgesMap, submittedBadgeIDs.length]
  );

  const passesClaimableFilter = useCallback(
    (badge: Badge) => isClaimableAgainstEnabledMap(badge, enabledBadgesMap),
    [isClaimableAgainstEnabledMap, enabledBadgesMap]
  );

  const passesRarityFilter = useCallback(
    (badge: Badge) => !rarityFilters || rarityFilters.size === 0 || rarityFilters.has(badge.rarityName),
    [rarityFilters]
  );

  const passesBadgeFilters = useCallback(
    (badge: Badge, groupName?: string) =>
      passesClaimableFilter(badge) && passesSearchFilter(badge, groupName) && passesRarityFilter(badge),
    [passesClaimableFilter, passesSearchFilter, passesRarityFilter]
  );

  const renderClaimBadge = (
    badge: Badge,
    opts?: {
      key?: string;
      journeyCompletionHint?: { emoji: string; lines: string[] };
      forceVisible?: boolean;
      departing?: boolean;
      fromSuggestion?: boolean;
    }
  ) => {
    const {
      blackAndWhite,
      display,
      lowOpacity,
      disableInteraction,
      showName,
      designIncluded,
      ideaIncluded,
      cursorNotAllowed,
    } = getBadgeProperties({
      badge,
      mode,
      enabledBadgesMap,
      badgeCreditsSubmissions,
      badgeDesignSubmissions,
      profileBadgeIDs,
      submittedBadgeIDSet,
      badgeCreditSubmissionIDSet,
      badgeDesignSubmissionIDSet,
      profileBadgeIDSet,
    });

    const isUnclaimedInEdit = mode === 'editing_profile' && !enabledBadgesMap.get(badge.id);
    const departing = !!opts?.departing;

    const lockInfo =
      isUnclaimedInEdit && !departing
        ? getExtraBadgeDescription(badge, badgesMap, enabledBadgesMap, submittedBadgeIDs.length)
        : null;

    // Edit/create: show global claim % on hover for every unclaimed badge.
    const claimPctLabel = isUnclaimedInEdit && !departing ? formatBadgeClaimPercent(badge) : null;

    return (
      <div
        key={opts?.key ?? badge.id}
        className={`gallery-badge rarity-${badge.rarityName}${departing ? ' gallery-badge--departing' : ''}`}
      >
        <BadgeComponent
          badge={badge}
          extraDescription={lockInfo}
          highlight={hoveredUnlockRequirementIdSet.has(badge.id)}
          unlockKeyNudge={
            unlockWaveActiveId === badge.id ? (hoveredUnlockRequirementIds.length === 1 ? 'loop' : 'once') : false
          }
          onHoverChange={(isHovered) => setUnlockRequirementsHovered(lockInfo?.requiredBadgeIds, isHovered)}
          justUnlocked={justUnlockedIds.has(badge.id)}
          journeyCompletionHint={departing ? null : opts?.journeyCompletionHint}
          onClick={opts?.fromSuggestion ? (clicked) => handleOnClickBadge(clicked, true) : handleOnClickBadge}
          blackAndWhite={departing ? false : blackAndWhite}
          display={opts?.forceVisible || departing ? true : display}
          smaller={true}
          showFloatingNameShadow={false}
          lowOpacity={opts?.forceVisible || departing ? false : lowOpacity}
          showNameHeight={true}
          enableInteraction={!departing}
          showName={showName}
          hiddenName={disableInteraction && mode === 'editing_profile' && !departing}
          designIncluded={designIncluded}
          ideaIncluded={ideaIncluded}
          initiallyHidden={mode === 'editing_profile' && !enabledBadgesMap.get(badge.id) && !departing}
          isEditing={mode === 'editing_profile'}
          cursorNotAllowed={departing ? true : cursorNotAllowed}
          newlyClaimed={
            departing ||
            (mode === 'editing_profile' && !!enabledBadgesMap.get(badge.id) && !profileBadgeIDs?.includes(badge.id))
          }
          removed={
            mode === 'editing_profile' && !!profileBadgeIDs?.includes(badge.id) && !enabledBadgesMap.get(badge.id)
          }
          compactInEditMode={mode === 'editing_profile' && !enabledBadgesMap.get(badge.id) && !departing}
          showUsefulLinksOnHover={mode === 'editing_profile' && !departing}
          claimPctLabel={claimPctLabel}
          onUnlockTargetClick={handleUnlockTargetClick}
        />
      </div>
    );
  };

  const matchingJourneyBadges = useMemo(
    () =>
      mode === 'editing_profile' && showJourneyCompletingSection
        ? journeyCompletingBadges.filter(({ badge }) => passesBadgeFilters(badge))
        : [],
    [mode, showJourneyCompletingSection, journeyCompletingBadges, passesBadgeFilters]
  );

  // Apply UI filters first, then cap at 12 (so rarity pills refill the section).
  const matchingClaimedByOthersBadges = useMemo(() => {
    if (mode !== 'editing_profile' || !showClaimedByOthersSection) return [];

    const ranked = claimedByOthersEligible.filter((badge) => passesBadgeFilters(badge));
    const MAX = 12;
    const TOP = 6;

    if (!hasClaimedOfficeCheckIn) return ranked.slice(0, MAX);

    const top6 = ranked.filter((badge) => !officeCheckInIds.has(badge.id)).slice(0, TOP);
    const top6Ids = new Set(top6.map((badge) => badge.id));
    const remainder = ranked.filter((badge) => !top6Ids.has(badge.id));
    return [...top6, ...remainder].slice(0, MAX);
  }, [
    mode,
    showClaimedByOthersSection,
    claimedByOthersEligible,
    passesBadgeFilters,
    hasClaimedOfficeCheckIn,
    officeCheckInIds,
  ]);

  const matchingRecentlyAddedBadges = useMemo(
    () =>
      mode === 'editing_profile' && showRecentlyAddedSection
        ? recentlyAddedEligible.filter((badge) => passesBadgeFilters(badge)).slice(0, 6)
        : [],
    [mode, showRecentlyAddedSection, recentlyAddedEligible, passesBadgeFilters]
  );

  matchingClaimedByOthersRef.current = matchingClaimedByOthersBadges;
  matchingJourneySuggestionsRef.current = matchingJourneyBadges;
  matchingRecentlyAddedRef.current = matchingRecentlyAddedBadges;

  // Keep clicked tile in place while departing; swap in the refilled list only after fade-out.
  const displayedClaimedByOthersBadges = frozenClaimedByOthers ?? matchingClaimedByOthersBadges;
  const displayedJourneyBadges = frozenJourneySuggestions ?? matchingJourneyBadges;
  const displayedRecentlyAddedBadges = frozenRecentlyAdded ?? matchingRecentlyAddedBadges;
  const journeySuggestionRarityCounts = useMemo(
    () => countUniqueJourneyRarities(displayedJourneyBadges.map(({ journey }) => journey)),
    [displayedJourneyBadges]
  );

  // Parents that pass visibleGroups already sort by claim rate — don't redo it.
  const orderedGroups = useMemo(
    () => visibleGroups ?? sortBadgeGroupsByGlobalClaimRate(BADGE_GROUPS, badgesMap),
    [visibleGroups, badgesMap]
  );
  const hasActiveFilters = !!searchQuery || (rarityFilters != null && rarityFilters.size > 0);

  const partitionBadgesForClaimFilter = useCallback(
    (allBadges: Badge[], groupName?: string) => {
      const sortBadges = (list: Badge[]) =>
        [...list].sort((a, b) => compareForceSortLast(a, b) || compareBadgesByScoreDesc(a, b));

      // Claimed / hidden tiles only in edit/create.
      if (mode !== 'editing_profile') {
        const badges = sortBadges(allBadges.filter((badge) => passesBadgeFilters(badge, groupName)));
        return {
          claimedBadges: [] as Badge[],
          badges,
          hiddenBadges: [] as Badge[],
        };
      }

      const claimedBadges: Badge[] = [];
      const mainBadges: Badge[] = [];
      const hiddenBadges: Badge[] = [];

      for (const badge of allBadges) {
        if (!passesSearchFilter(badge, groupName) || !passesRarityFilter(badge)) {
          continue;
        }
        // “Claimed” tile = owned before this edit only. Session claims stay in the main grid.
        if (profileBadgeIDSet.has(badge.id)) {
          claimedBadges.push(badge);
          continue;
        }
        // Soft-hide (e.g. other 2023 offsite): claimable, but collapsed into “hidden” — not locked.
        // Use profile baseline only so claiming one mid-edit doesn’t bounce the sibling into hidden.
        if (!enabledBadgesMap.get(badge.id) && isSoftHiddenBySiblingOffsite(badge.id, profileBadgeIDSet)) {
          hiddenBadges.push(badge);
          continue;
        }
        // Main grid: claimable (or just claimed this session). Locked → expandable “hidden” tile.
        if (enabledBadgesMap.get(badge.id) || passesClaimableFilter(badge)) {
          mainBadges.push(badge);
        } else {
          hiddenBadges.push(badge);
        }
      }

      return {
        claimedBadges: sortBadges(claimedBadges),
        badges: sortBadges(mainBadges),
        hiddenBadges: sortBadges(hiddenBadges),
      };
    },
    [
      mode,
      passesBadgeFilters,
      passesSearchFilter,
      passesRarityFilter,
      passesClaimableFilter,
      enabledBadgesMap,
      profileBadgeIDSet,
    ]
  );

  const visibleGroupSections = useMemo(
    () =>
      orderedGroups
        .filter((group) => (mode === 'submitted' ? group.badgesIDs.some((id) => submittedBadgeIDSet.has(id)) : true))
        .map((group) => {
          const claimedCount = group.badgesIDs.filter((id) => enabledBadgesMap.get(id)).length;
          const percentage = !showPercentages
            ? 100
            : group.badgesIDs.length === 0
              ? 0
              : Math.round((claimedCount / group.badgesIDs.length) * 100);
          const allBadges = group.badgesIDs
            .map((id) => badgesMap.get(id))
            .filter((badge): badge is Badge => badge != null);
          const { claimedBadges, badges, hiddenBadges } = partitionBadgesForClaimFilter(allBadges, group.name);
          // Edit/claim: remaining vs the saved profile. Hidden/unreleased stay out until they unhide;
          // session clicks stay in until summarize.
          const rarityBadgeIds =
            mode === 'editing_profile'
              ? allBadges
                  .filter((badge) =>
                    isEditGroupRarityCountable(
                      badge,
                      badgesMap,
                      profileBadgeIDSet,
                      enabledBadgesMap,
                      submittedBadgeIDs.length
                    )
                  )
                  .map((badge) => badge.id)
              : group.badgesIDs;
          return {
            group,
            claimedCount,
            percentage,
            claimedBadges,
            badges,
            hiddenBadges,
            rarityBadgeIds,
          };
        })
        .filter(
          (section) => section.badges.length > 0 || section.claimedBadges.length > 0 || section.hiddenBadges.length > 0
        ),
    [
      orderedGroups,
      mode,
      submittedBadgeIDSet,
      enabledBadgesMap,
      showPercentages,
      badgesMap,
      partitionBadgesForClaimFilter,
      profileBadgeIDSet,
      submittedBadgeIDs.length,
    ]
  );

  const { freeformClaimedBadges, freeformBadges, freeformHiddenBadges } = useMemo(() => {
    const allFreeformBadges = showFreeformBadges
      ? badgesNotInAnyBadgeGroup.map((id) => badgesMap.get(id)).filter((badge): badge is Badge => badge != null)
      : [];
    const { claimedBadges, badges, hiddenBadges } = partitionBadgesForClaimFilter(allFreeformBadges);
    return {
      freeformClaimedBadges: claimedBadges,
      freeformBadges: badges,
      freeformHiddenBadges: hiddenBadges,
    };
  }, [showFreeformBadges, badgesNotInAnyBadgeGroup, badgesMap, partitionBadgesForClaimFilter]);

  // Edit mode: person-specific group order from unclaimed + not-hidden claim %. After a claim,
  // groups at/above that category stay put (no vertical jump); groups after it re-sort.
  const { claimableGroupSections, deferredGroupSections } = useMemo(() => {
    if (mode !== 'editing_profile') {
      return {
        claimableGroupSections: visibleGroupSections,
        deferredGroupSections: [] as typeof visibleGroupSections,
      };
    }
    if (visibleGroupSections.length === 0) {
      return {
        claimableGroupSections: [],
        deferredGroupSections: [] as typeof visibleGroupSections,
      };
    }

    const layout = resolveEditProfileGroupLayout(
      BADGE_GROUPS,
      badgesMap,
      [...profileBadgeIDSet],
      liveOwnedBadgeIds,
      submittedBadgeIDs.length,
      editLayoutFrozenPrefix
    );

    const sectionByName = new Map(visibleGroupSections.map((section) => [section.group.name, section]));
    const orderedSections: typeof visibleGroupSections = [];
    for (const name of layout.order) {
      const section = sectionByName.get(name);
      if (section) orderedSections.push(section);
    }
    for (const section of visibleGroupSections) {
      if (!layout.order.includes(section.group.name)) orderedSections.push(section);
    }

    const claimable: typeof visibleGroupSections = [];
    const deferred: typeof visibleGroupSections = [];
    for (const section of orderedSections) {
      if (layout.deferredNames.has(section.group.name)) deferred.push(section);
      else claimable.push(section);
    }
    return {
      claimableGroupSections: claimable,
      deferredGroupSections: deferred,
    };
  }, [
    mode,
    visibleGroupSections,
    badgesMap,
    profileBadgeIDSet,
    liveOwnedBadgeIds,
    submittedBadgeIDs.length,
    editLayoutFrozenPrefix,
  ]);

  const hasVisibleBadges =
    displayedJourneyBadges.length > 0 ||
    displayedClaimedByOthersBadges.length > 0 ||
    displayedRecentlyAddedBadges.length > 0 ||
    visibleGroupSections.length > 0 ||
    freeformBadges.length > 0 ||
    freeformClaimedBadges.length > 0 ||
    freeformHiddenBadges.length > 0;

  const hasJourneyBadges = displayedJourneyBadges.length > 0;
  const journeyBodyOpen = hasJourneyBadges && !journeyClaimCollapsed;

  const hasClaimedByOthersBadges = displayedClaimedByOthersBadges.length > 0;
  const claimedByOthersBodyOpen = hasClaimedByOthersBadges && !claimedByOthersCollapsed;

  const hasRecentlyAddedBadges = displayedRecentlyAddedBadges.length > 0;
  const recentlyAddedBodyOpen = hasRecentlyAddedBadges && !recentlyAddedCollapsed;

  const toggleJourneyClaimCollapsed = () => {
    setJourneyClaimCollapsedState((prev) => {
      const next = !prev;
      setJourneyClaimCollapsed(next);
      return next;
    });
  };

  const toggleClaimedByOthersCollapsed = () => {
    setClaimedByOthersCollapsedState((prev) => {
      const next = !prev;
      setClaimedByOthersCollapsed(next);
      return next;
    });
  };

  const toggleRecentlyAddedCollapsed = () => {
    setRecentlyAddedCollapsedState((prev) => {
      const next = !prev;
      setRecentlyAddedCollapsed(next);
      return next;
    });
  };

  const toggleClaimedPartition = (key: string) => {
    setExpandedClaimedPartitions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleHiddenPartition = (key: string) => {
    setExpandedHiddenPartitions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const sectionChevron = (isOpen: boolean, opts?: { spacer?: boolean }) => (
    <span
      className={`claim-section-chevron${opts?.spacer ? ' claim-section-chevron--spacer' : isOpen ? ' open' : ' collapsed'}`}
      aria-hidden
    >
      {opts?.spacer ? null : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6">
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );

  const renderHiddenCountTile = (key: string, hiddenBadges: Badge[]) => {
    // Quick search: skip the stack — hidden badges render full-size as if stacks didn't exist.
    if (mode !== 'editing_profile' || hiddenBadges.length <= 0 || searchQuery) return null;
    const hiddenCount = hiddenBadges.length;
    const isOpen = expandedHiddenPartitions.has(key);
    // Collapsed only: nudge when the wave’s active key is inside this box.
    // Expanded / visible: the key badge itself nudges via `unlockKeyNudge`.
    const isRattling =
      !isOpen && unlockWaveActiveId != null && hiddenBadges.some((badge) => badge.id === unlockWaveActiveId);
    const isRattlingLoop = isRattling && hoveredUnlockRequirementIds.length === 1;
    return (
      <div
        className={`gallery-badge gallery-badge--hidden-by-filters${isOpen ? ' is-expanded' : ''}${isRattling ? ' is-rattling' : ''}${isRattlingLoop ? ' is-rattling-loop' : ''}`}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-label={`${hiddenCount} hidden. ${isOpen ? 'Collapse' : 'Expand'} to ${isOpen ? 'hide' : 'review'}.`}
        onClick={() => toggleHiddenPartition(key)}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleHiddenPartition(key);
          }
        }}
      >
        <div className="gallery-badge-hidden-tile">
          <span className="gallery-badge-hidden-count">{hiddenCount}</span>
          <span className="gallery-badge-hidden-label">{isOpen ? 'hidden · hide' : 'hidden'}</span>
        </div>
      </div>
    );
  };

  const renderClaimedCountTile = (key: string, claimedBadges: Badge[]) => {
    // Quick search: skip the stack — claimed badges render full-size as if stacks didn't exist.
    if (mode !== 'editing_profile' || claimedBadges.length <= 0 || searchQuery) return null;
    const claimedCount = claimedBadges.length;
    const isOpen = expandedClaimedPartitions.has(key);
    // Lowest score on top; next 1–2 peek out as a messy pile behind (same footprint).
    const stackBadges = [...claimedBadges]
      .sort((a, b) => (a.badgeScore ?? 0) - (b.badgeScore ?? 0))
      .slice(0, Math.min(3, claimedCount));
    const faceBadge = stackBadges[0];
    const peekBadges = stackBadges.slice(1);
    const faceImageUrl = faceBadge.smallImageUrl || faceBadge.imageUrl;
    const rarityName = faceBadge.rarityName || 'common';

    return (
      <div
        className={`gallery-badge gallery-badge--claimed-count rarity-${rarityName}${isOpen ? ' is-expanded' : ''}`}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-label={`${claimedCount} claimed. ${isOpen ? 'Collapse' : 'Expand'} to ${isOpen ? 'hide' : 'review'}.`}
        onClick={() => toggleClaimedPartition(key)}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleClaimedPartition(key);
          }
        }}
      >
        <div className="gallery-badge-claimed-tile">
          <div className="gallery-badge-claimed-stack" aria-hidden>
            {peekBadges.map((badge, i) => (
              <div
                key={badge.id}
                className={`gallery-badge-claimed-peek gallery-badge-claimed-peek--${peekBadges.length - i}`}
              >
                <img
                  className="gallery-badge-claimed-peek-img"
                  src={badge.smallImageUrl || badge.imageUrl}
                  alt=""
                  draggable={false}
                />
              </div>
            ))}
          </div>
          <div className="gallery-badge-claimed-face">
            <div className="gallery-badge-claimed-blur" aria-hidden>
              <img className="gallery-badge-claimed-blur-img" src={faceImageUrl} alt="" draggable={false} />
            </div>
            <span className="gallery-badge-claimed-count">{claimedCount}</span>
            <span className="gallery-badge-claimed-label">{isOpen ? 'claimed · hide' : 'claimed'}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderClaimFilterGrid = (key: string, claimedBadges: Badge[], badges: Badge[], hiddenBadges: Badge[]) => {
    // Quick search expands claimed/hidden stacks so matching badges show at full size.
    const claimedExpanded = !!searchQuery || expandedClaimedPartitions.has(key);
    const hiddenExpanded = !!searchQuery || expandedHiddenPartitions.has(key);
    return (
      <div className="gallery-badge-grid">
        {renderClaimedCountTile(key, claimedBadges)}
        {claimedExpanded
          ? claimedBadges.map((badge) => renderClaimBadge(badge, { key: `claimed-${key}-${badge.id}` }))
          : null}
        {badges.map((badge) => renderClaimBadge(badge))}
        {renderHiddenCountTile(key, hiddenBadges)}
        {hiddenExpanded
          ? hiddenBadges.map((badge) => renderClaimBadge(badge, { key: `hidden-${key}-${badge.id}` }))
          : null}
      </div>
    );
  };

  const renderGroupSection = ({
    group,
    claimedCount,
    percentage,
    claimedBadges,
    badges,
    hiddenBadges,
    rarityBadgeIds,
  }: (typeof visibleGroupSections)[number]) => (
    <section key={group.name} className="gallery-group" data-gallery-group={group.name}>
      <div className="gallery-group-fill" aria-hidden>
        <div
          className="gallery-group-fill-inner"
          style={{
            background: groupBackgroundGradient(group),
            opacity: percentage === 0 ? 0 : 0.28 + (percentage / 100) * 0.28,
            clipPath: `polygon(0 0, ${percentage}% 0, ${percentage === 0 ? 0 : percentage + 20}% 100%, 0 100%)`,
          }}
        />
      </div>
      <div className="gallery-group-content">
        <header className="gallery-group-head">
          <span
            className="gallery-group-swatch"
            style={{ background: groupBackgroundGradient(group, '160deg') }}
            aria-hidden
          />
          <div className="gallery-group-titles">
            <h2 className="gallery-group-name">{group.name}</h2>
            {showPercentages ? (
              <p className="gallery-group-meta">
                {`${claimedCount}/${group.badgesIDs.length} claimed · ${percentage}%`}
              </p>
            ) : null}
          </div>
          <GalleryGroupRarityCounts badgeIds={rarityBadgeIds} badgesMap={badgesMap} />
        </header>
        {renderClaimFilterGrid(group.name, claimedBadges, badges, hiddenBadges)}
      </div>
    </section>
  );

  return (
    <div className="collected-badges-container claim-badge-groups">
      <div className="collected-badges-inner-container">
        <div className="gallery-sections">
          {mode === 'editing_profile' && showJourneyCompletingSection ? (
            <section
              className={`gallery-group gallery-group--journey-claim${
                !hasJourneyBadges ? ' gallery-group--journey-claim-empty' : ''
              }`}
            >
              <header
                className={`gallery-group-head${hasJourneyBadges ? ' claim-section-head--toggle' : ''}`}
                {...(hasJourneyBadges
                  ? {
                      'role': 'button' as const,
                      'tabIndex': 0,
                      'aria-expanded': !journeyClaimCollapsed,
                      'onClick': toggleJourneyClaimCollapsed,
                      'onKeyDown': (e: KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleJourneyClaimCollapsed();
                        }
                      },
                    }
                  : {})}
              >
                <span
                  className={`gallery-group-swatch ${
                    !hasJourneyBadges ? 'gallery-group-swatch--muted' : 'gallery-group-swatch--journey'
                  }`}
                  aria-hidden
                />
                <div className="gallery-group-titles">
                  <h2 className="gallery-group-name">Complete a Journey - Bonus Points!</h2>
                  <p className="gallery-group-meta">
                    {!hasJourneyBadges
                      ? hasActiveFilters
                        ? 'No journey-completing badges match your filters.'
                        : 'No journeys ready to complete yet — claim more badges to complete one.'
                      : journeyClaimCollapsed
                        ? 'Expand to see badges that complete a journey.'
                        : 'Claim any of these badges to complete a journey.'}
                  </p>
                </div>
                <GalleryRarityCounts counts={journeySuggestionRarityCounts} />
                {hasJourneyBadges ? sectionChevron(!journeyClaimCollapsed) : sectionChevron(false, { spacer: true })}
              </header>
              <div className={`journey-claim-body${journeyBodyOpen ? ' is-open' : ''}`} aria-hidden={!journeyBodyOpen}>
                <div className="journey-claim-body-inner">
                  <div className="gallery-badge-grid">
                    {displayedJourneyBadges.map(({ badge, journey }) => {
                      const score = getRarityScoreFromName(journey.rarity);
                      return renderClaimBadge(badge, {
                        key: `${journey.id}-${badge.id}`,
                        forceVisible: true,
                        departing: departingSuggestionIds.has(badge.id),
                        fromSuggestion: true,
                        journeyCompletionHint: {
                          emoji: journey.emoji,
                          lines: [`+${score} bonus point${score > 1 ? 's' : ''}!`],
                        },
                      });
                    })}
                  </div>
                </div>
              </div>
            </section>
          ) : null}

          {mode === 'editing_profile' && showRecentlyAddedSection && hasRecentlyAddedBadges ? (
            <section className="gallery-group gallery-group--recently-added">
              <header
                className="gallery-group-head claim-section-head--toggle"
                role="button"
                tabIndex={0}
                aria-expanded={!recentlyAddedCollapsed}
                onClick={toggleRecentlyAddedCollapsed}
                onKeyDown={(e: KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleRecentlyAddedCollapsed();
                  }
                }}
              >
                <span className="gallery-group-swatch gallery-group-swatch--recently-added" aria-hidden />
                <div className="gallery-group-titles">
                  <h2 className="gallery-group-name">Recently added</h2>
                  <p className="gallery-group-meta">
                    {recentlyAddedCollapsed
                      ? 'Expand to see recently added badges.'
                      : 'New badges since your last profile update.'}
                  </p>
                </div>
                <GalleryGroupRarityCounts
                  badgeIds={displayedRecentlyAddedBadges.map((badge) => badge.id)}
                  badgesMap={badgesMap}
                />
                {sectionChevron(!recentlyAddedCollapsed)}
              </header>
              <div
                className={`journey-claim-body${recentlyAddedBodyOpen ? ' is-open' : ''}`}
                aria-hidden={!recentlyAddedBodyOpen}
              >
                <div className="journey-claim-body-inner">
                  <div className="gallery-badge-grid">
                    {displayedRecentlyAddedBadges.map((badge) =>
                      renderClaimBadge(badge, {
                        key: `recently-added-${badge.id}`,
                        forceVisible: true,
                        departing: departingSuggestionIds.has(badge.id),
                        fromSuggestion: true,
                      })
                    )}
                  </div>
                </div>
              </div>
            </section>
          ) : null}

          {mode === 'editing_profile' && showClaimedByOthersSection ? (
            <section
              className={`gallery-group gallery-group--claimed-by-others${
                !hasClaimedByOthersBadges ? ' gallery-group--claimed-by-others-empty' : ''
              }`}
            >
              <header
                className={`gallery-group-head${hasClaimedByOthersBadges ? ' claim-section-head--toggle' : ''}`}
                {...(hasClaimedByOthersBadges
                  ? {
                      'role': 'button' as const,
                      'tabIndex': 0,
                      'aria-expanded': !claimedByOthersCollapsed,
                      'onClick': toggleClaimedByOthersCollapsed,
                      'onKeyDown': (e: KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleClaimedByOthersCollapsed();
                        }
                      },
                    }
                  : {})}
              >
                <span
                  className={`gallery-group-swatch ${
                    !hasClaimedByOthersBadges
                      ? 'gallery-group-swatch--muted'
                      : 'gallery-group-swatch--claimed-by-others'
                  }`}
                  aria-hidden
                />
                <div className="gallery-group-titles">
                  <h2 className="gallery-group-name">Claimed by others</h2>
                  <p className="gallery-group-meta">
                    {!hasClaimedByOthersBadges
                      ? hasActiveFilters
                        ? 'No suggested badges match your filters.'
                        : 'No popular unclaimed badges to suggest yet — check back as more people claim.'
                      : claimedByOthersCollapsed
                        ? "Expand to see popular badges you haven't claimed yet."
                        : "Popular badges you haven't claimed yet."}
                  </p>
                </div>
                <GalleryGroupRarityCounts
                  badgeIds={displayedClaimedByOthersBadges.map((badge) => badge.id)}
                  badgesMap={badgesMap}
                />
                {hasClaimedByOthersBadges
                  ? sectionChevron(!claimedByOthersCollapsed)
                  : sectionChevron(false, { spacer: true })}
              </header>
              <div
                className={`journey-claim-body${claimedByOthersBodyOpen ? ' is-open' : ''}`}
                aria-hidden={!claimedByOthersBodyOpen}
              >
                <div className="journey-claim-body-inner">
                  <div className="gallery-badge-grid">
                    {displayedClaimedByOthersBadges.map((badge) =>
                      renderClaimBadge(badge, {
                        key: `claimed-by-others-${badge.id}`,
                        forceVisible: true,
                        departing: departingSuggestionIds.has(badge.id),
                        fromSuggestion: true,
                      })
                    )}
                  </div>
                </div>
              </div>
            </section>
          ) : null}

          {claimableGroupSections.map(renderGroupSection)}

          {freeformBadges.length > 0 || freeformClaimedBadges.length > 0 || freeformHiddenBadges.length > 0 ? (
            <section className="gallery-group gallery-group--freeform" data-gallery-group="More Badges">
              <header className="gallery-group-head">
                <span className="gallery-group-swatch gallery-group-swatch--muted" aria-hidden />
                <div className="gallery-group-titles">
                  <h2 className="gallery-group-name">More Badges</h2>
                  <p className="gallery-group-meta">
                    {`${badgesNotInAnyBadgeGroup.filter((id) => enabledBadgesMap.get(id)).length}/${
                      badgesNotInAnyBadgeGroup.length
                    } claimed`}
                  </p>
                </div>
                <GalleryGroupRarityCounts
                  badgeIds={
                    mode === 'editing_profile'
                      ? badgesNotInAnyBadgeGroup.filter((id) => {
                          const badge = badgesMap.get(id);
                          return (
                            !!badge &&
                            isEditGroupRarityCountable(
                              badge,
                              badgesMap,
                              profileBadgeIDSet,
                              enabledBadgesMap,
                              submittedBadgeIDs.length
                            )
                          );
                        })
                      : badgesNotInAnyBadgeGroup
                  }
                  badgesMap={badgesMap}
                />
              </header>
              {renderClaimFilterGrid('freeform', freeformClaimedBadges, freeformBadges, freeformHiddenBadges)}
            </section>
          ) : null}

          {deferredGroupSections.map(renderGroupSection)}

          {hasActiveFilters && mode === 'editing_profile' && !hasVisibleBadges ? (
            <p className="claim-badge-search-empty" role="status">
              {searchQuery ? `No badges match “${badgeSearchQuery.trim()}”.` : 'No badges match your filters.'}
            </p>
          ) : null}
        </div>
      </div>
      {mode === 'submitted' && submittedBadgeIDs?.length === 0 && (
        <div className="empty-submissions">
          <p>None contributed yet.</p>
          <button className="main-button" onClick={() => window.open(BADGE_SUBMISSION_URL, '_blank')}>
            <p>
              Submit a Badge Idea <span className="talos-green">◆</span>
            </p>
          </button>
          <div className="h-stack">
            {/* Incentives for new badge submittion, Badger and Badge Baron */}
            <BadgeComponent badge={badgesMap.get('65ry')!} onClick={onClickBadge} smaller={true} />
            <BadgeComponent badge={badgesMap.get('uwpa')!} onClick={onClickBadge} smaller={true} />
          </div>
        </div>
      )}
    </div>
  );
};
